import { apiCall, isBackendConfigured } from './api-call'
import { buildSyntheticUser, readUser, writeUser } from './local-store'
import {
  QuickPassUser,
  CreateOrUpdateQuickPassUserInput,
} from '../_types/quickpass-user'

const warnFallback = (operation: string, error: unknown): void => {
  console.warn(
    `[quickpass-user] backend ${operation} failed, falling back to localStorage —`,
    error,
  )
}

/**
 * Looks up the QuickPass user record for a given Firebase Auth UID.
 * Returns null when the user has not yet been provisioned. Falls back to
 * local storage when no backend is configured OR when the backend call
 * throws (network error, CORS, 5xx).
 *
 * @param {string} firebaseAuthUid
 * @returns {Promise<QuickPassUser | null>}
 */
const getQuickPassUserByFirebaseUid = async (
  firebaseAuthUid: string,
): Promise<QuickPassUser | null> => {
  if (isBackendConfigured() === false) {
    return readUser(firebaseAuthUid)
  }

  try {
    const response = await apiCall({
      group: 'quickpass',
      command: 'getuserbyfirebaseuid',
      data: { firebaseAuthUid },
    })

    if (response.code === 404) {
      return null
    }

    if (response.code !== 200) {
      throw new Error(response.message)
    }

    return JSON.parse(response.data) as QuickPassUser
  } catch (error) {
    warnFallback('getUserByFirebaseUid', error)
    return readUser(firebaseAuthUid)
  }
}

/**
 * Idempotent upsert. Falls back to local storage when no backend is
 * configured OR when the backend call throws.
 *
 * @param {CreateOrUpdateQuickPassUserInput} input
 * @returns {Promise<QuickPassUser>}
 */
const createOrUpdateQuickPassUser = async (
  input: CreateOrUpdateQuickPassUserInput,
): Promise<QuickPassUser> => {
  if (isBackendConfigured() === true) {
    try {
      const response = await apiCall({
        group: 'quickpass',
        command: 'createorupdateuser',
        data: {
          firebaseAuthUid: input.firebaseAuthUid,
          email: input.email,
          phone: input.phone,
          displayName: input.displayName,
          authProviders: input.authProviders,
        },
      })

      if (response.code !== 200) {
        throw new Error(response.message)
      }

      return JSON.parse(response.data) as QuickPassUser
    } catch (error) {
      warnFallback('createOrUpdateUser', error)
    }
  }

  const existing = readUser(input.firebaseAuthUid)

  if (existing !== null) {
    const merged: QuickPassUser = {
      ...existing,
      email: input.email !== '' ? input.email : existing.email,
      phone: input.phone ?? existing.phone,
      displayName: input.displayName ?? existing.displayName,
      authProviders: Array.from(
        new Set([...existing.authProviders, ...input.authProviders]),
      ),
      updatedAt: Date.now(),
      lastSignInAt: Date.now(),
    }
    writeUser(merged)
    return merged
  }

  const synthetic = buildSyntheticUser(input.firebaseAuthUid, {
    email: input.email,
    phone: input.phone,
    displayName: input.displayName,
    authProviders: input.authProviders,
  })
  writeUser(synthetic)
  return synthetic
}

/**
 * Patches a QuickPass user. Falls back to local storage when backend
 * call throws.
 *
 * @param {string} id
 * @param {Partial<QuickPassUser>} updates
 * @returns {Promise<QuickPassUser>}
 */
const updateQuickPassUser = async (
  id: string,
  updates: Partial<QuickPassUser>,
): Promise<QuickPassUser> => {
  if (isBackendConfigured() === true) {
    try {
      const response = await apiCall({
        group: 'quickpass',
        command: 'updateuser',
        data: { id, updates },
      })

      if (response.code !== 200) {
        throw new Error(response.message)
      }

      return JSON.parse(response.data) as QuickPassUser
    } catch (error) {
      warnFallback('updateUser', error)
    }
  }

  throw new Error(
    'updateQuickPassUser is not yet implemented in the local-storage fallback.',
  )
}

export {
  getQuickPassUserByFirebaseUid,
  createOrUpdateQuickPassUser,
  updateQuickPassUser,
}
